import { computeShowingRange } from "../formatting/pagination";
import { Combobox } from "./Combobox";

/**
 * Labeled pagination (design.md §6 "Pagination has labeled previous/next
 * controls, current-page text, total, and a page-size control; disabled
 * ends remain semantically disabled.").
 *
 * `variant="range"` (REDISENO-PANEL.md §7.1, "Pagination restyled"): swaps
 * the status text for "Mostrando a–b de total" and hides the page-size
 * selector (the prototype's Contacts footer has neither a "Página X de Y"
 * phrasing nor a size picker) -- additive and opt-in, defaulting to
 * `"default"` so every existing caller (campaigns/templates/segments) keeps
 * its exact current text and page-size control untouched.
 */
export function Pagination({
  page,
  size,
  total,
  onPageChange,
  onSizeChange,
  pageSizeOptions = [20, 50],
  variant = "default",
}: {
  page: number;
  size: number;
  total: number;
  onPageChange: (page: number) => void;
  onSizeChange: (size: number) => void;
  pageSizeOptions?: readonly number[];
  variant?: "default" | "range";
}) {
  const totalPages = Math.max(1, Math.ceil(total / size));
  const { from, to } = computeShowingRange(page, size, total);

  return (
    <nav aria-label="Paginación" className="v2-pagination">
      <button
        type="button"
        className="v2-u-target-min"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
      >
        Anterior
      </button>
      <span aria-live="polite" className="v2-pagination__status v2-u-tabular-nums">
        {variant === "range" ? (
          <>
            Mostrando {from}–{to} de {total}
          </>
        ) : (
          <>
            Página {page} de {totalPages} · {total} en total
          </>
        )}
      </span>
      <button
        type="button"
        className="v2-u-target-min"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
      >
        Siguiente
      </button>
      {variant === "default" && (
        <>
          <label htmlFor="v2-pagination-size">Tamaño de página</label>
          <Combobox
            id="v2-pagination-size"
            className="v3-combobox--inline"
            searchable={false}
            value={String(size)}
            onChange={(next) => onSizeChange(Number(next))}
            options={pageSizeOptions.map((option) => ({ value: String(option), label: String(option) }))}
          />
        </>
      )}
    </nav>
  );
}
